// TODO: Write tests for this file

import React from "react";
import { parse as parseComments, Block as CommentBlock } from "comment-parser";

type ParsedProp = {
  propLineIndex: number;
  propName: string;
  propType: string;
  propDescription: string;
  isPropRequired: boolean;
  isPropDeprecated: boolean;
};

const findCommentBlock = (commentBlocks: CommentBlock[], lineIndex: number) => {
  return commentBlocks.find((commentBlock) => {
    const commentBlockEndLineIndex =
      commentBlock.source[commentBlock.source.length - 1].number;
    return commentBlockEndLineIndex === lineIndex - 1;
  });
};

export const parsePropsFile = (fileContents: string): ParsedProp[] => {
  const lines = fileContents.split("\n");
  const linesReversed = [...lines].reverse();
  const lastLineIndex = lines.length - 1;

  const propsStartLineIndex =
    lastLineIndex -
    linesReversed.findIndex((line) => line.includes("Props = "));

  if (propsStartLineIndex === -1) {
    throw new Error(
      "Could not determine props. Unable to determine propsStartLineIndex",
    );
  }

  if (lines[propsStartLineIndex].endsWith("Props = Record<string, never>;")) {
    return [];
  }

  if (lines[propsStartLineIndex].endsWith("};")) {
    if (lines[propsStartLineIndex].endsWith("{};")) {
      return [];
    } else {
      throw new Error(
        "Props cannot be one line. Try adding some documentation comments.",
      );
    }
  }

  const propsEndLineIndex =
    propsStartLineIndex +
    lines.slice(propsStartLineIndex).findIndex((line) => line === "};");

  if (propsEndLineIndex === -1) {
    throw new Error(
      "Could not determine props. Unable to determine propsEndLineIndex",
    );
  }

  const propLines = lines.slice(propsStartLineIndex, propsEndLineIndex + 1);

  const propComments = parseComments(fileContents);

  const parsedProps = propLines
    .map((line, lineIndex): ParsedProp | undefined => {
      if (!line.match(/^ {2}\w/)) {
        return undefined;
      }

      const firstColonIndex = line.indexOf(":");
      if (firstColonIndex === -1) {
        throw new Error(
          "Could not determine props. Unable to determine first colon.",
        );
      }
      const keyPart = line.slice(0, firstColonIndex);
      const valuePart = line.slice(firstColonIndex + 1);

      const propLineIndex = propsStartLineIndex + lineIndex;
      const propName = keyPart.trim().replace("?", "");
      let propType = valuePart.trim().replace(";", "");
      const isPropRequired = !keyPart.endsWith("?");

      if (propType === "{") {
        propType = "--COMPLEX_OBJECT--";
      } else if (propType === "(" || propType === "") {
        propType = "--COMPLEX_TYPE--";
      }

      const propCommentBlock = findCommentBlock(propComments, propLineIndex);
      const propDescription = propCommentBlock?.description || "";
      const isPropDeprecated = !!propCommentBlock?.tags.find(
        (tag) => tag.tag === "deprecated",
      );

      return {
        propLineIndex,
        propName,
        propType,
        propDescription,
        isPropRequired,
        isPropDeprecated,
      };
    })
    .filter((x) => !!x)
    .map((x) => x as NonNullable<typeof x>);

  return parsedProps;
};

export const formatCode = (code: string): string => {
  const codeLines = code.split("\n");
  if (!codeLines.length) {
    return "";
  }

  const firstNonEmptyLine = codeLines.find(
    (codeLine) => codeLine.trim().length,
  );
  if (!firstNonEmptyLine) {
    return "";
  }

  const isFirstLineEmpty = !codeLines[0].trim().length;
  const isLastLineEmpty = !codeLines[codeLines.length - 1].trim().length;

  const initialIndent = firstNonEmptyLine
    ? firstNonEmptyLine.length - firstNonEmptyLine.trimStart().length
    : 0;

  const formattedCodeLines = codeLines.map((codeLine) =>
    codeLine.trim().length ? `  ${codeLine.slice(initialIndent)}` : "",
  );

  const firstLine = isFirstLineEmpty ? "" : "\n";
  const lastLine = isLastLineEmpty ? "" : "\n";

  return `${firstLine}${formattedCodeLines.join("\n")}${lastLine}`;
};

export const formatBoolean = (bool: boolean): string => {
  return bool ? "Yes" : "No";
};

export const formatPropType = (propType: string): React.ReactNode => {
  if (propType === "--COMPLEX_OBJECT--") {
    return <>Object</>;
  }
  if (propType === "--COMPLEX_TYPE--") {
    return <>Complex Type</>;
  }
  return <pre className="text-pre-wrap">{propType}</pre>;
};

export const formatPropName = (parsedProp: ParsedProp): React.ReactNode => {
  if (parsedProp.isPropDeprecated) {
    return <s>{parsedProp.propName}</s>;
  }
  return <>{parsedProp.propName}</>;
};
